/* Kasho Pro premium storefront interactions. Vanilla, no dependencies. */
(() => {
  const d = document;
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 })
    : null;

  const initReveal = (scope) => {
    scope.querySelectorAll('[data-kp-reveal]:not(.is-in)').forEach((el) => (io ? io.observe(el) : el.classList.add('is-in')));
  };

  const stepOf = (track) => {
    const first = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first ? first.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
  };

  const initRails = (scope) => {
    scope.querySelectorAll('[data-kp-rail]').forEach((wrap) => {
      if (wrap.dataset.kpInit) return;
      const track = wrap.querySelector('[data-kp-track]');
      if (!track) return;
      wrap.dataset.kpInit = '1';
      const prev = wrap.querySelector('[data-kp-prev]');
      const next = wrap.querySelector('[data-kp-next]');
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
        wrap.classList.toggle('kp-rail--static', max <= 2);
      };
      prev && prev.addEventListener('click', () => track.scrollBy({ left: -stepOf(track), behavior: 'smooth' }));
      next && next.addEventListener('click', () => track.scrollBy({ left: stepOf(track), behavior: 'smooth' }));
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      track.kpUpdate = update;
      update();
    });
  };

  const initTabs = (scope) => {
    scope.querySelectorAll('[data-kp-tabs]').forEach((wrap) => {
      if (wrap.dataset.kpInit) return;
      wrap.dataset.kpInit = '1';
      const tabs = [...wrap.querySelectorAll('[role="tab"]')];
      const panels = [...wrap.querySelectorAll('[role="tabpanel"]')];
      const prev = wrap.querySelector('[data-kp-tab-prev]');
      const next = wrap.querySelector('[data-kp-tab-next]');
      const current = () => { const p = panels.find((x) => !x.hidden); return p ? p.querySelector('[data-kp-track]') : null; };
      const sync = () => {
        const t = current();
        if (!t) return;
        const max = t.scrollWidth - t.clientWidth - 2;
        if (prev) prev.disabled = t.scrollLeft <= 2;
        if (next) next.disabled = t.scrollLeft >= max;
        wrap.classList.toggle('kp-rail--static', max <= 2);
      };
      prev && prev.addEventListener('click', () => { const t = current(); if (t) t.scrollBy({ left: -stepOf(t), behavior: 'smooth' }); });
      next && next.addEventListener('click', () => { const t = current(); if (t) t.scrollBy({ left: stepOf(t), behavior: 'smooth' }); });
      panels.forEach((p) => { const t = p.querySelector('[data-kp-track]'); if (t) t.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true }); });
      window.addEventListener('resize', sync);
      const activate = (i, focus) => {
        tabs.forEach((t, j) => { t.setAttribute('aria-selected', String(j === i)); t.tabIndex = j === i ? 0 : -1; });
        panels.forEach((p, j) => { p.hidden = j !== i; });
        const p = panels[i];
        if (p) {
          p.querySelectorAll('[data-kp-reveal]').forEach((el) => el.classList.add('is-in'));
          const t = p.querySelector('[data-kp-track]');
          if (t) { t.scrollLeft = 0; if (t.kpUpdate) t.kpUpdate(); }
        }
        sync();
        if (focus && tabs[i]) tabs[i].focus();
      };
      tabs.forEach((t, i) => {
        t.addEventListener('click', () => activate(i));
        t.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); activate((i + 1) % tabs.length, true); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); activate((i - 1 + tabs.length) % tabs.length, true); }
        });
      });
      activate(0);
    });
  };

  const initHero = (scope) => {
    scope.querySelectorAll('[data-kp-hero]').forEach((hero) => {
      if (hero.dataset.kpInit) return;
      hero.dataset.kpInit = '1';
      const slides = [...hero.querySelectorAll('[data-kp-slide]')];
      const dots = [...hero.querySelectorAll('[data-kp-dot]')];
      if (slides.length < 2) { hero.classList.remove('is-ticking'); return; }
      let i = 0; let timer = null;
      const delay = (parseInt(hero.dataset.autoplay, 10) || 0) * 1000;
      const restartProgress = () => { hero.classList.remove('is-ticking'); void hero.offsetWidth; if (delay && !reduce()) hero.classList.add('is-ticking'); };
      const go = (n) => {
        slides[i].classList.remove('is-active'); slides[i].setAttribute('aria-hidden', 'true'); slides[i].inert = true;
        if (dots[i]) dots[i].setAttribute('aria-current', 'false');
        i = (n + slides.length) % slides.length;
        slides[i].querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
        slides[i].classList.add('is-active'); slides[i].removeAttribute('aria-hidden'); slides[i].inert = false;
        if (dots[i]) dots[i].setAttribute('aria-current', 'true');
        restartProgress();
      };
      const play = () => { if (!delay || reduce()) { hero.classList.remove('is-ticking'); return; } clearInterval(timer); timer = setInterval(() => go(i + 1), delay); restartProgress(); };
      const stop = () => { clearInterval(timer); hero.classList.remove('is-ticking'); };
      slides.forEach((s, j) => { if (j) s.inert = true; });
      const prev = hero.querySelector('[data-kp-hero-prev]');
      const next = hero.querySelector('[data-kp-hero-next]');
      prev && prev.addEventListener('click', () => { go(i - 1); play(); });
      next && next.addEventListener('click', () => { go(i + 1); play(); });
      dots.forEach((dot, j) => dot.addEventListener('click', () => { go(j); play(); }));
      if (window.matchMedia('(hover: hover)').matches) {
        hero.addEventListener('mouseenter', stop);
        hero.addEventListener('mouseleave', play);
      }
      hero.addEventListener('focusin', stop);
      let x0 = null; let y0 = null;
      hero.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
      hero.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0; const dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) { go(i + (dx < 0 ? 1 : -1)); play(); }
        x0 = null;
      }, { passive: true });
      d.addEventListener('visibilitychange', () => (d.hidden ? stop() : play()));
      play();
    });
  };

  const initCount = (scope) => {
    scope.querySelectorAll('[data-kp-count]').forEach((el) => {
      if (el.dataset.kpInit) return;
      el.dataset.kpInit = '1';
      const target = parseFloat(el.dataset.kpCount) || 0;
      const run = () => {
        if (reduce()) { el.textContent = target; return; }
        const t0 = performance.now(); const dur = 1400;
        const tick = (t) => { const p = Math.min(1, (t - t0) / dur); el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3))); if (p < 1) requestAnimationFrame(tick); };
        requestAnimationFrame(tick);
      };
      if ('IntersectionObserver' in window) {
        const o = new IntersectionObserver((es) => { if (es[0].isIntersecting) { run(); o.disconnect(); } }, { threshold: 0.4 });
        o.observe(el);
      } else { run(); }
    });
  };

  /* Copy discount code */
  d.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-kp-copy]');
    if (!btn) return;
    e.preventDefault();
    const code = btn.dataset.kpCopy;
    try { await navigator.clipboard.writeText(code); } catch (_) {
      const ta = d.createElement('textarea'); ta.value = code; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
      d.body.appendChild(ta); ta.select(); try { d.execCommand('copy'); } catch (__) {} ta.remove();
    }
    const label = btn.querySelector('[data-kp-copy-label]');
    if (label && !label.dataset.kpOld) label.dataset.kpOld = label.textContent;
    btn.classList.add('is-copied');
    if (label) label.textContent = 'Copied';
    clearTimeout(btn.kpT);
    btn.kpT = setTimeout(() => { btn.classList.remove('is-copied'); if (label) label.textContent = label.dataset.kpOld; }, 2200);
  });

  /* Quick add to cart from product cards */
  d.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-kp-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.classList.contains('is-loading')) return;
    const id = btn.dataset.kpAdd;
    btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true');
    const finish = (ok) => {
      btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy');
      if (ok) { btn.classList.add('is-added'); setTimeout(() => btn.classList.remove('is-added'), 1800); }
    };
    try {
      const A = window.Shopify && window.Shopify.actions;
      if (A && typeof A.updateCart === 'function') {
        const res = await A.updateCart({ lines: [{ merchandiseId: String(id), quantity: 1 }] });
        if (res && res.userErrors && res.userErrors.length) throw new Error(res.userErrors[0].message);
        finish(true);
        if (typeof A.openCart === 'function') A.openCart();
      } else {
        const r = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }) });
        if (!r.ok) throw new Error('Add to cart failed');
        finish(true);
        window.location.href = root + 'cart';
      }
    } catch (err) {
      console.warn('[kasho] quick add failed, opening product page', err);
      finish(false);
      if (btn.dataset.kpFallback) window.location.href = btn.dataset.kpFallback;
    }
  });

  /* Read more toggles */
  d.addEventListener('click', (e) => {
    const b = e.target.closest('[data-kp-more]');
    if (!b) return;
    const box = d.getElementById(b.getAttribute('aria-controls'));
    if (!box) return;
    const open = b.getAttribute('aria-expanded') === 'true';
    b.setAttribute('aria-expanded', String(!open));
    box.classList.toggle('is-open', !open);
    const l = b.querySelector('[data-kp-more-label]');
    if (l) l.textContent = open ? (b.dataset.moreText || 'Read more') : (b.dataset.lessText || 'Show less');
  });

  /* Sort auto-submit + filter drawer */
  d.addEventListener('change', (e) => {
    const el = e.target.closest('[data-kp-autosubmit]');
    if (el && el.form) { if (el.form.requestSubmit) el.form.requestSubmit(); else el.form.submit(); }
  });
  d.addEventListener('click', (e) => {
    const open = e.target.closest('[data-kp-dialog-open]');
    if (open) { const dlg = d.getElementById(open.dataset.kpDialogOpen); if (dlg && dlg.showModal) { dlg.showModal(); d.documentElement.classList.add('kp-lock'); } return; }
    const close = e.target.closest('[data-kp-dialog-close]');
    if (close) { const dlg = close.closest('dialog'); if (dlg) dlg.close(); return; }
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('kp-drawer')) e.target.close();
  });
  d.addEventListener('close', (e) => { if (e.target.classList && e.target.classList.contains('kp-drawer')) d.documentElement.classList.remove('kp-lock'); }, true);

  const init = (scope) => { initReveal(scope); initRails(scope); initTabs(scope); initHero(scope); initCount(scope); };
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', () => init(d)); else init(d);
  d.addEventListener('shopify:section:load', (e) => init(e.target));
})();
